import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUserTie, FaSearch, FaPhone, FaEnvelope,
  FaMapMarkerAlt, FaGlobe, FaFax, FaArrowLeft, FaBuilding
} from 'react-icons/fa';
import './PublicDefenders.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PublicDefenders = () => {
  console.log('=== PublicDefenders Component Rendering ===');
  console.log('Current URL:', window.location.href);
  console.log('Component initialized');

  const [offices, setOffices] = useState([]);
  const [groupedOffices, setGroupedOffices] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    console.log('PublicDefenders component mounted - useEffect running');
    fetchPublicDefenders();
  }, []);

  useEffect(() => {
    // Group offices by region
    const grouped = {};
    offices.forEach(office => {
      const region = office.region || 'Unknown';
      if (!grouped[region]) {
        grouped[region] = [];
      }
      grouped[region].push(office);
    });
    setGroupedOffices(grouped);
  }, [offices]);

  const fetchPublicDefenders = async () => {
    try {
      console.log('Fetching public defenders data...');
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/address-book/public-defenders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('Public defenders response:', data);

      if (data.success) {
        console.log('Successfully loaded', data.data.length, 'offices');
        setOffices(data.data);
        setError('');
      } else {
        console.log('Failed to load public defenders:', data.message);
        setError('Failed to load public defender offices');
      }
    } catch (err) {
      console.error('Error fetching public defenders:', err);
      setError('Failed to load public defender offices');
    } finally {
      setLoading(false);
    }
  };

  const filteredRegions = Object.keys(groupedOffices).filter(region =>
    region.toLowerCase().includes(searchTerm.toLowerCase()) ||
    groupedOffices[region].some(office =>
      office.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (office.counties_served && office.counties_served.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (office.physical_city && office.physical_city.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  ).sort((a, b) => {
    // Sort Central first, then numeric regions, then alphabetic
    if (a === 'Central') return -1;
    if (b === 'Central') return 1;
    const aNum = parseInt(a);
    const bNum = parseInt(b);
    if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
    if (!isNaN(aNum)) return -1;
    if (!isNaN(bNum)) return 1;
    return a.localeCompare(b);
  });

  const getOfficeTypeBadge = (officeType) => {
    if (officeType === 'Regional Office') {
      return { label: 'Regional', color: '#16a085' };
    } else if (officeType === 'Family Defense') {
      return { label: 'Family Defense', color: '#9b59b6' };
    } else if (officeType === 'Conflict Office') {
      return { label: 'Conflict', color: '#e67e22' };
    } else if (officeType === 'Division Administration') {
      return { label: 'Division', color: '#3498db' };
    } else if (officeType === 'Central Office') {
      return { label: 'Central Admin', color: '#2c3e50' };
    } else {
      return { label: officeType, color: '#95a5a6' };
    }
  };

  const getRegionName = (region) => {
    if (region === 'Central') return 'Central Administration';
    if (!isNaN(parseInt(region))) return `Region ${region}`;
    return region;
  };

  if (loading) {
    return (
      <div className="public-defenders-container">
        <div className="loading">Loading public defender offices...</div>
      </div>
    );
  }

  return (
    <div className="public-defenders-container">
      {/* Header */}
      <div className="public-defenders-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/address-book')}>
            <FaArrowLeft />
          </button>
          <FaUserTie size={32} />
          <div>
            <h1>Montana Office of State Public Defender</h1>
            <p className="subtitle">{filteredRegions.length} regions • {offices.length} offices</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="public-defenders-search">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by region, office name, city, or counties served..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Regions List */}
      <div className="regions-list">
        {filteredRegions.length === 0 ? (
          <div className="no-results">
            <FaUserTie size={48} />
            <p>No public defender offices found matching your search</p>
          </div>
        ) : (
          filteredRegions.map(region => {
            const regionOffices = groupedOffices[region];

            return (
              <div key={region} className="region-card">
                <div className="region-header">
                  <h2>{getRegionName(region)}</h2>
                  <span className="region-stats">
                    {regionOffices.length} {regionOffices.length === 1 ? 'office' : 'offices'}
                  </span>
                </div>

                <div className="offices-grid">
                  {regionOffices.map(office => {
                    const badge = getOfficeTypeBadge(office.office_type);

                    return (
                      <div key={office.id} className="office-card">
                        <div className="office-header">
                          <div className="office-name-wrapper">
                            <FaBuilding className="office-icon" />
                            <div>
                              <h3>{office.organization_name}</h3>
                              <span
                                className="office-type-badge"
                                style={{ backgroundColor: badge.color }}
                              >
                                {badge.label}
                              </span>
                            </div>
                          </div>
                        </div>

                        {office.counties_served && (
                          <div className="counties-served">
                            <strong>Counties Served:</strong> {office.counties_served}
                          </div>
                        )}

                        <div className="contact-details">
                          {office.physical_street && (
                            <div className="contact-item">
                              <FaMapMarkerAlt className="contact-icon" />
                              <span>
                                {office.physical_street}
                                {office.physical_city && `, ${office.physical_city}`}
                                {office.physical_state && `, ${office.physical_state}`}
                                {office.physical_zip && ` ${office.physical_zip}`}
                              </span>
                            </div>
                          )}

                          {office.mailing_street && (
                            <div className="contact-item mailing">
                              <FaMapMarkerAlt className="contact-icon" />
                              <span>
                                <strong>Mailing:</strong> {office.mailing_street}
                                {office.mailing_city && `, ${office.mailing_city}`}
                                {office.mailing_state && `, ${office.mailing_state}`}
                                {office.mailing_zip && ` ${office.mailing_zip}`}
                              </span>
                            </div>
                          )}

                          {office.phone && (
                            <div className="contact-item">
                              <FaPhone className="contact-icon" />
                              <a href={`tel:${office.phone}`}>{office.phone}</a>
                            </div>
                          )}

                          {office.fax && (
                            <div className="contact-item">
                              <FaFax className="contact-icon" />
                              <span>{office.fax}</span>
                            </div>
                          )}

                          {office.main_email && (
                            <div className="contact-item">
                              <FaEnvelope className="contact-icon" />
                              <a href={`mailto:${office.main_email}`}>{office.main_email}</a>
                            </div>
                          )}

                          {office.website && (
                            <div className="contact-item">
                              <FaGlobe className="contact-icon" />
                              <a href={office.website} target="_blank" rel="noopener noreferrer">
                                {office.website}
                              </a>
                            </div>
                          )}
                        </div>

                        {office.notes && (
                          <div className="office-notes">
                            <small>{office.notes}</small>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PublicDefenders;
